import {Notification} from '@app/gen/schemas/notification';
import {NotificationData} from '@app/gen/schemas/notification-data';
import {SiteConfigContext} from '@common/core/settings/site-config-context';
import {notificationDestination} from '@common/notifications/notification-destination';
import {NotificationLine} from '@common/notifications/notification-line';
import {NotificationsDialogContext} from '@common/notifications/notifications-dialog';
import {
  deleteNotificationsOptions,
  markNotificationsAsReadOptions,
} from '@common/notifications/notifications-queries';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import {Button, LinkButton} from '@shadcn/button/button';
import {useMutation} from '@tanstack/react-query';
import {MixedImage} from '@ui/images/mixed-image';
import {useSettings} from '@ui/settings/use-settings';
import {isAbsoluteUrl} from '@ui/utils/urls/is-absolute-url';
import clsx from 'clsx';
import {
  CheckIcon,
  DownloadIcon,
  EyeIcon,
  LockIcon,
  Trash2Icon,
  UserRoundPlus,
  UsersRound,
  XIcon,
} from 'lucide-react';
import React, {ComponentProps, JSXElementConstructor, use} from 'react';

const iconMap = {
  'group-add': UserRoundPlus,
  people: UsersRound,
  'export-csv': DownloadIcon,
  visibility: EyeIcon,
  lock: LockIcon,
  check: CheckIcon,
  close: XIcon,
  file: DownloadIcon,
} as Record<string, JSXElementConstructor<ComponentProps<'svg'>>>;

interface NotificationListProps {
  notifications: Notification[];
  className?: string;
}
export function NotificationList({
  notifications,
  className,
}: NotificationListProps) {
  const {notifications: config} = use(SiteConfigContext);

  return (
    <div className={className}>
      {notifications.map((notification, index) => {
        const isLast = notifications.length - 1 === index;
        const Renderer =
          config?.renderMap?.[notification.type] || NotificationListItem;
        return (
          <Renderer
            key={notification.id}
            notification={notification}
            isLast={isLast}
          />
        );
      })}
    </div>
  );
}

export interface NotificationListItemProps {
  notification: Notification;
  onActionButtonClick?: ButtonActionsProps['onActionClick'];
  lineIconRenderer?: JSXElementConstructor<{icon: string}>;
  isLast: boolean;
}
export function NotificationListItem({
  notification,
  onActionButtonClick,
  lineIconRenderer,
  isLast,
}: NotificationListItemProps) {
  const markAsRead = useMutation(markNotificationsAsReadOptions());
  const deleteNotification = useMutation(deleteNotificationsOptions());
  const navigate = useNavigate();
  const dialog = use(NotificationsDialogContext);
  const destination = notificationDestination(notification);

  const showUnreadIndicator = !notification.data.image && !notification.read_at;

  const openDestination = () => {
    if (!destination) {
      return;
    }
    dialog.setOpen(false);
    if (isAbsoluteUrl(destination)) {
      window.open(destination, '_blank')?.focus();
      return;
    }
    navigate(destination);
  };

  return (
    <div
      role={destination ? 'link' : undefined}
      onClick={() => {
        if (!markAsRead.isPending && !notification.read_at) {
          markAsRead.mutate({ids: [notification.id]});
        }
        openDestination();
      }}
      className={clsx(
        'group relative flex items-start gap-3.5 bg-muted px-8 py-5',
        !isLast && 'border-b',
        destination && 'cursor-pointer',
        !notification.read_at
          ? 'bg-background hover:bg-primary/10'
          : 'hover:bg-muted/50',
      )}
      title={
        notification.data.mainAction?.label ||
        (destination ? 'Open' : undefined)
      }
    >
      {showUnreadIndicator && (
        <div className="absolute top-6.5 left-4 size-2 shrink-0 rounded-full bg-primary shadow-sm" />
      )}
      {notification.data.image && (
        <MixedImage
          className="size-5 shrink-0 text-muted-foreground"
          src={iconMap[notification.data.image] || notification.data.image}
        />
      )}
      <div className="min-w-0 flex-auto">
        {notificationLines(notification.data).map((line, index) => (
          <NotificationLine
            iconRenderer={lineIconRenderer}
            notification={notification}
            line={line}
            index={index}
            key={index}
          />
        ))}
        <ButtonActions
          onActionClick={onActionButtonClick}
          notification={notification}
        />
      </div>
      <Button
        variant="ghost"
        size="icon-xs"
        className="mt-0.5 shrink-0 text-muted-foreground"
        aria-label="Delete notification"
        disabled={deleteNotification.isPending}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          deleteNotification.mutate([notification.id]);
        }}
      >
        <Trash2Icon className="size-4" />
      </Button>
    </div>
  );
}

interface ButtonActionsProps {
  notification: Notification;
  onActionClick?: (
    e: React.MouseEvent,
    action: NonNullable<NotificationData['buttonActions']>[number],
  ) => void;
}
function ButtonActions({notification, onActionClick}: ButtonActionsProps) {
  const {base_url} = useSettings();
  const dialog = use(NotificationsDialogContext);
  if (!notification.data.buttonActions) return null;

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      {notification.data.buttonActions.map((action, index) => {
        const variant = index === 0 ? 'default' : 'outline';
        const color =
          action.color === 'secondary'
            ? undefined
            : action.color || (index === 0 ? 'primary' : undefined);

        if (action.emitOnly && !onActionClick) {
          return null;
        }

        if (!onActionClick && !action.emitOnly) {
          return (
            <LinkButton
              key={index}
              size="xs"
              variant={variant}
              color={color}
              to={action.action.replace(base_url, '')}
              onClick={e => {
                e.stopPropagation();
                dialog.setOpen(false);
              }}
            >
              {action.label}
            </LinkButton>
          );
        }

        return (
          <Button
            key={index}
            size="xs"
            variant={variant}
            color={color}
            onClick={e => {
              e.stopPropagation();
              onActionClick?.(e, action);
            }}
          >
            {action.label}
          </Button>
        );
      })}
    </div>
  );
}

function notificationLines(data: NotificationData) {
  if (data.lines?.length) {
    return data.lines;
  }
  const raw = data as NotificationData & {
    name?: string;
    email?: string;
    topic?: string;
    body?: string;
    message?: string;
  };
  const lines: NonNullable<NotificationData['lines']> = [];
  const name = raw.name ?? '';
  const email = raw.email ?? '';
  if (name || email) {
    lines.push({
      content:
        name && email
          ? `Contact from ${name} (${email})`
          : `Contact from ${name}${email}`,
      type: 'primary',
    });
  }
  if (raw.topic) {
    lines.push({content: raw.topic, type: 'secondary'});
  }
  const body = raw.body ?? raw.message;
  if (body) {
    lines.push({content: body, type: 'secondary'});
  }
  return lines;
}
